import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API, rupiah, fmtDate, errMsg, ORDER_STATUS, imgSrc, PAYMENT_METHODS, bankList } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Package } from "lucide-react";

const TRACK_STEPS = ["menunggu_ongkir", "menunggu_pembayaran", "menunggu_konfirmasi", "diproses", "dikirim", "selesai"];

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [settings, setSettings] = useState(null);
  const fileRef = useRef({});

  const load = () => API.get("/orders/my").then((r) => setOrders(r.data)).catch(() => setOrders([]));

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate("/masuk?next=/pesanan");
      return;
    }
    load();
    API.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const uploadProof = async (orderId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/upload", fd);
      await API.post(`/orders/${orderId}/payment`, { proof_url: data.url });
      toast.success("Bukti pembayaran terunggah. Menunggu konfirmasi pemilik.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const cancel = async (orderId) => {
    try {
      await API.post(`/orders/${orderId}/cancel`);
      toast.success("Pesanan dibatalkan");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-10" data-testid="orders-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h1 className="font-display text-3xl tracking-tight mb-8">Pesanan Saya</h1>
        {orders === null ? (
          <p className="text-slate-500">Memuat...</p>
        ) : orders.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-14 text-center" data-testid="orders-empty">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-6">Belum ada pesanan.</p>
            <Link to="/toko" className="inline-block px-8 py-3 bg-[#0B1F3A] text-white font-bold text-sm rounded-md">Mulai Belanja</Link>
          </div>
        ) : (
          <div className="space-y-5" data-testid="orders-list">
            {orders.map((o) => {
              const st = ORDER_STATUS[o.status] || { label: o.status, color: "bg-slate-100 text-slate-700" };
              return (
                <div key={o.id} data-testid={`order-${o.id}`} className="bg-white border border-slate-200 rounded-md p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="font-mono-accent text-sm font-bold text-[#0B1F3A]">{o.order_number}</p>
                      <p className="text-xs text-slate-400">{fmtDate(o.created_at)}</p>
                    </div>
                    <span data-testid={`order-status-${o.id}`} className={`px-3 py-1.5 rounded-full text-xs font-bold ${st.color}`}>{st.label}</span>
                  </div>
                  {o.status !== "dibatalkan" && (
                    <ol className="flex items-start overflow-x-auto pb-2 mb-2" data-testid={`order-timeline-${o.id}`}>
                      {TRACK_STEPS.map((sKey, i) => {
                        const idx = TRACK_STEPS.indexOf(o.status);
                        const done = i <= idx;
                        return (
                          <li key={sKey} className="flex items-center" data-testid={`order-step-${o.id}-${sKey}`}>
                            <div className="flex flex-col items-center">
                              <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center text-[9px] font-bold ${done ? "bg-[#0B1F3A] border-[#0B1F3A] text-white" : "border-slate-300 text-slate-400"}`}>{i + 1}</div>
                              <span className={`text-[9px] mt-1 whitespace-nowrap ${i === idx ? "font-bold text-[#0B1F3A]" : "text-slate-400"}`}>{ORDER_STATUS[sKey].label}</span>
                            </div>
                            {i < TRACK_STEPS.length - 1 && <div className={`h-0.5 w-6 sm:w-10 mx-1 mb-4 ${i < idx ? "bg-[#0B1F3A]" : "bg-slate-200"}`} />}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <div className="space-y-2 border-t border-slate-100 pt-4">
                    {o.items.map((i, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-600">{i.name} ({i.variant_label}) × {i.qty}</span>
                        <span className="font-semibold">{rupiah(i.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-100 mt-4 pt-4 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{rupiah(o.subtotal)}</span></div>
                    {(o.voucher_discount || 0) > 0 && (
                      <div className="flex justify-between text-emerald-700"><span>Voucher {o.voucher_code}</span><span>- {rupiah(o.voucher_discount)}</span></div>
                    )}
                    {(o.discount || 0) > 0 && (
                      <div className="flex justify-between text-emerald-700"><span>Diskon dari Pemilik</span><span>- {rupiah(o.discount)}</span></div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ongkir{o.delivery_method === "ambil_sendiri" ? " (Ambil Sendiri)" : o.courier ? ` (${o.courier})` : ""}</span>
                      <span>{o.shipping_cost > 0 ? rupiah(o.shipping_cost) : o.delivery_method === "ambil_sendiri" ? "Gratis" : "Menunggu pemilik"}</span>
                    </div>
                    <div className="flex justify-between font-bold text-base pt-1"><span>Total</span><span data-testid={`order-total-${o.id}`}>{rupiah(o.total)}</span></div>
                    <div className="flex justify-between text-xs pt-1">
                      <span className="text-slate-500">Metode Pembayaran</span>
                      <span className="font-semibold" data-testid={`order-payment-${o.id}`}>
                        {o.payment_method === "hutang" ? `Hutang — jatuh tempo ${o.due_date || "-"}` : (PAYMENT_METHODS[o.payment_method] || PAYMENT_METHODS.transfer).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Metode Pengiriman</span>
                      <span className="font-semibold" data-testid={`order-delivery-${o.id}`}>
                        {o.delivery_method === "ambil_sendiri" ? "Ambil Sendiri di Toko" : "Pakai Jasa Pengiriman"}
                      </span>
                    </div>
                  </div>
                  {o.payment_method === "hutang" && ["menunggu_pembayaran", "diproses", "dikirim"].includes(o.status) && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900" data-testid={`hutang-info-${o.id}`}>
                      Pembayaran <strong>Hutang/Tempo</strong> — jatuh tempo <strong>{o.due_date}</strong>. Mohon lunasi sebelum jatuh tempo.
                    </div>
                  )}
                  {o.status === "menunggu_pembayaran" && o.payment_method === "transfer" && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-4 text-sm" data-testid={`payment-info-${o.id}`}>
                      <p className="font-bold text-amber-900 mb-2">Silakan transfer ke salah satu rekening resmi:</p>
                      {bankList(settings).length ? (
                        <ul className="space-y-1.5" data-testid={`bank-list-${o.id}`}>
                          {bankList(settings).map((b, i) => (
                            <li key={i} className="text-amber-800" data-testid={`bank-item-${o.id}-${i}`}>{b.bank_name} — <span className="font-mono-accent font-bold">{b.account_number}</span> a.n. {b.holder}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-amber-800">Hubungi pemilik via WhatsApp untuk info rekening pembayaran.</p>
                      )}
                      <p className="text-amber-800 mt-2">Kemudian unggah bukti transfer di bawah ini.</p>
                    </div>
                  )}
                  {o.status === "menunggu_pembayaran" && o.payment_method === "qris" && (
                    <div className="mt-4 bg-purple-50 border border-purple-200 rounded-md p-4 text-sm" data-testid={`payment-info-${o.id}`}>
                      <p className="font-bold text-purple-900 mb-2">Scan kode QRIS berikut untuk membayar:</p>
                      {settings?.qris_image ? (
                        <img src={imgSrc(settings.qris_image)} alt="QRIS NAWADAH" className="h-48 rounded-md border border-purple-200 object-contain bg-white" data-testid={`qris-image-${o.id}`} />
                      ) : (
                        <p className="text-purple-800">Kode QRIS belum tersedia — hubungi pemilik via WhatsApp.</p>
                      )}
                      <p className="text-purple-800 mt-2">Setelah membayar, unggah bukti pembayaran QRIS di bawah ini.</p>
                    </div>
                  )}
                  {o.status === "menunggu_pembayaran" && o.payment_method === "cash" && (
                    <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm text-emerald-900" data-testid={`payment-info-${o.id}`}>
                      Pembayaran <strong>Cash / Tunai</strong> — siapkan pembayaran tunai saat barang diterima atau diambil di toko.
                    </div>
                  )}
                  {o.payment_proof && (
                    <a href={imgSrc(o.payment_proof)} target="_blank" rel="noreferrer" className="mt-3 inline-block text-xs text-blue-600 hover:underline" data-testid={`proof-link-${o.id}`}>
                      Lihat bukti pembayaran
                    </a>
                  )}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {o.payment_method !== "hutang" && (o.status === "menunggu_pembayaran" || o.status === "menunggu_konfirmasi") && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={(el) => (fileRef.current[o.id] = el)}
                          onChange={(e) => uploadProof(o.id, e.target.files[0])}
                          data-testid={`proof-input-${o.id}`}
                        />
                        <button
                          onClick={() => fileRef.current[o.id]?.click()}
                          data-testid={`upload-proof-${o.id}`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-xs font-bold rounded-md transition-colors"
                        >
                          <Upload className="h-3.5 w-3.5" /> Unggah Bukti Pembayaran
                        </button>
                      </>
                    )}
                    {(o.status === "menunggu_ongkir" || o.status === "menunggu_pembayaran") && (
                      <button onClick={() => cancel(o.id)} data-testid={`cancel-order-${o.id}`} className="px-5 py-2.5 border border-red-300 text-red-600 text-xs font-bold rounded-md hover:bg-red-50 transition-colors">
                        Batalkan Pesanan
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
